import { createTheme } from '@mui/material';
import { colors } from '../utils/palette';

declare module '@mui/material/styles' {
  interface Theme {
    constants: {
      headerHeight: number;
      menuWidth: number;
      menuWidthSm: number;
    };
  }

  interface ThemeOptions {
    constants: {
      headerHeight: number;
      menuWidth: number;
      menuWidthSm: number;
    };
  }
}

declare module '@mui/material/styles/createPalette' {
  export interface PaletteOptions {
    sidebarBG: string;
    sidebarBgWithAlpha: string;
  }

  export interface Palette {
    sidebarBG: string;
    sidebarBgWithAlpha: string;
  }
}

const sharedThemeOptions = {
  constants: {
    headerHeight: 109,
    menuWidth: 280,
    menuWidthSm: 64,
  },
  typography: {
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
      lineHeight: 1.167,
      letterSpacing: '-0.24px',
      color: colors.secondary,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: '44px',
      letterSpacing: '-0.6px',
      color: colors.secondary,
    },
    h3: {
      fontSize: '1.5rem ',
      fontWeight: '500 ',
      lineHeight: '1.167 ',
      letterSpacing: '-0.06px ',
      color: colors.secondary,
    },
    h4: {
      fontSize: '1.2rem ',
      fontWeight: '500 ',
      lineHeight: '1.235 ',
      letterSpacing: '-0.06px ',
      color: colors.secondary,
    },
    h5: {
      fontSize: '16px ',
      fontWeight: '500 ',
      lineHeight: '1.334 ',
      letterSpacing: '-0.05px ',
      color: colors.secondary,
    },
    h6: {
      fontSize: '14px ',
      color: colors.secondary,
    },
    button: {
      textTransform: 'none',
    },
    body2: {
      color: colors.textSecondary,
    },
  },
  spacing: (value: number) => value * 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiSelect: { styleOverrides: { select: { '&:focus': { backgroundColor: 'transparent' } } } },
    MuiCollapse: {
      styleOverrides: {
        root: { width: '100%' },
        wrapper: { width: '100%', maxWidth: '100%' },
        wrapperInner: { width: '100%', maxWidth: '100%' },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          body: { lineHeight: 1 },
          html: { WebkitFontSmoothing: 'auto' },
        },
      },
    },
    MuiDialogActions: { styleOverrides: { root: { padding: 24 } } },
    MuiCardHeader: { styleOverrides: { root: { padding: 24 } } },
    MuiTypography: { styleOverrides: { overline: { fontWeight: 500 } } },
    MuiAccordionDetails: { styleOverrides: { root: { paddingLeft: '24px' } } },
    MuiAccordionSummary: { styleOverrides: { root: { paddingLeft: '24px' } } },
    MuiButton: { styleOverrides: { root: { boxShadow: 'none' } } },
    MuiAvatar: {
      styleOverrides: {
        root: { alignItems: 'unset' },
        colorDefault: { color: '#000', backgroundColor: 'transparent' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorRight: { backgroundColor: '#f5f5f5', width: '70%' },
        paperAnchorLeft: { backgroundColor: '#FFF', width: '256px' },
        paperAnchorDockedLeft: { borderRight: 'none' },
      },
    },
    MuiListSubheader: {
      styleOverrides: { gutters: { paddingLeft: 0, paddingRight: 0 } },
    },
    MuiSlider: {
      styleOverrides: {
        track: { border: 0 },
        valueLabel: { fontWeight: 400 },
      },
    },
    MuiListItem: {
      styleOverrides: {
        gutters: { paddingLeft: 8, paddingRight: 8 },
        button: { borderRadius: '4px' },
        root: { marginTop: 0, marginBottom: 0 },
      },
    },
    MuiListItemText: {
      styleOverrides: { root: { color: colors.textSecondary } },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0px 10px 20px -8px rgba(216, 210, 218, 0.25)',
        },
        elevation10: {
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.31), 0 7px 12px -4px rgba(0,0,0,0.25) !important',
        },
      },
    },
    MuiTab: { styleOverrides: { root: { width: 160 } } },
    MuiToolbar: {
      styleOverrides: {
        regular: { height: '64px', minHeight: '64px' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: '6px', borderRadius: '5px' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: 0,
          borderBottom: '1px solid rgb(245 245 245)',
          borderCollapse: 'unset',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            background: '#F3F4F6',
            textTransform: 'uppercase',
            fontWeight: 500,
            border: 'none',
            fontSize: 14,
            lineHeight: 1.5,
            ':last-child': { textAlign: 'end' },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': { height: `83px` },
          '& .MuiTableCell-root': {
            padding: '16px',
            fontWeight: 400,
            fontSize: 14,
            lineHeight: 1.4,
            ':last-child': { textAlign: 'end' },
          },
        },
      },
    },
    MuiInputBase: { styleOverrides: { root: { fontSize: 14 } } },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '&>div': {
            '&:hover': { cursor: 'pointer' },
          },
        },
      },
    },
    MuiFormLabel: { styleOverrides: { root: { fontSize: 14 } } },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiDialog-paper': {
            maxHeight: 'calc(100% - 10px)',
            margin: '5px 24px',
            borderRadius: '1px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& label.Mui-focused': { color: '#546E7A' },
          '& .MuiInput-underline:after': { borderBottomColor: '#546E7A' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
            '&:hover fieldset': { borderColor: '#111827' },
            '&.Mui-focused fieldset': { borderColor: '#111827' },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#111827',
          },
          '&.MuiFormLabel-root.Mui-focused': {
            color: '#111827',
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: false },
      styleOverrides: {
        tooltip: { backgroundColor: colors.gray3 },
        arrow: { color: colors.gray3 },
      },
    },
  },
};

export const lightThemeOptions: any = {
  ...sharedThemeOptions,
  palette: {
    mode: 'light',
    divider: colors.bordersGray,
    grey: {
      '50': colors.gray0,
      '100': colors.gray1,
      '200': colors.gray2,
      '300': colors.gray3,
      '400': colors.gray4,
      '500': colors.gray5,
      '600': colors.gray6,
      '700': colors.gray7,
    },
    text: {
      primary: colors.secondary,
      secondary: colors.textSecondary,
    },
    primary: {
      main: colors.primary,
      dark: colors.darkPrimary,
      light: colors.lightPrimary,
    },
    secondary: {
      main: colors.secondary,
      light: colors.lightSecondary,
    },
    background: { default: colors.pageBackground },
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    sidebarBG: colors.secondary,
    sidebarBgWithAlpha: colors.lightSecondary,
  },
};

export const lightTheme = createTheme(lightThemeOptions);

const darkThemeOptions: any = {
  ...sharedThemeOptions,
  palette: {
    mode: 'dark',
    divider: colors.gray6,
    grey: {
      '50': colors.gray7,
      '100': colors.gray6,
      '200': colors.gray5,
      '300': colors.gray4,
      '400': colors.gray3,
      '500': colors.gray2,
      '600': colors.gray1,
      '700': colors.gray0,
    },
    text: {
      primary: colors.gray0,
      secondary: colors.gray3,
    },
    primary: {
      main: colors.primary,
      dark: colors.lightPrimary,
      light: colors.darkPrimary,
    },
    secondary: {
      main: colors.secondary,
      light: colors.lightSecondary,
    },
    background: { default: '#121212' },
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    sidebarBG: '#1f1f1f',
    sidebarBgWithAlpha: 'rgba(31, 31, 31, 0.9)',
  },
};

export const darkTheme = createTheme(darkThemeOptions);

export type ThemeMode = 'light' | 'dark';
